/*
 * CampaignBaselineReport.js
 *
 * WHAT: Views the immutable Role & Privilege BASELINE frozen at campaign
 *       submission (§Layer2, 2026-09-24). Two tabs: Roles (every role with its
 *       level, DBA certification, and the members who held it) and Privileges
 *       (every privilege the DBA certified with its level + certification).
 * WHY:  The audit surface — the baseline the managers' user-attestations were
 *       made against. Data is read-only from campaign_role_snapshot /
 *       campaign_privilege_snapshot; it is captured at submission and never
 *       changes. This is also where role -> members (holders) is visible.
 */
import React, { useState, useEffect } from 'react';
import API_CONFIG from '../config/api.config';
import { formatDateTime } from '../utils/dateUtils';

const LEVEL_CLASS = {
  SUPER_ADMIN: 'text-danger', ADMIN: 'text-danger', WRITE_ELEVATED: 'text-warning',
  WRITE: 'text-warning', READ_ELEVATED: 'text-info', READ: 'text-info',
  NONE: 'text-muted', UNASSIGNED: 'text-secondary',
};

const SCOPE_LABEL = {
  FIXED: 'Fixed / Built-in', PREDEFINED: 'Predefined (maintained)',
  SYSTEM: 'System', USER: 'User-defined', DERIVED: 'Derived',
};

const CampaignBaselineReport = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [tab, setTab] = useState('roles');
  const [roleData, setRoleData] = useState(null);
  const [privData, setPrivData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({}); // role row -> show members

  useEffect(() => { loadCampaigns(); }, []);

  const loadCampaigns = async () => {
    setIsLoadingCampaigns(true);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/'));
      const data = await response.json();
      if (data.campaigns) {
        // Baseline is captured at submission, so only ATTESTATION/COMPLETED
        // campaigns have one.
        setCampaigns(data.campaigns.filter(
          c => c.status === 'ATTESTATION' || c.status === 'COMPLETED'
        ));
      } else {
        setError('Failed to load campaigns');
      }
    } catch (err) {
      setError('Error loading campaigns: ' + err.message);
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const loadBaseline = async (campaignId) => {
    if (!campaignId) { setRoleData(null); setPrivData(null); return; }
    setIsLoading(true);
    setError(null);
    try {
      const [rRes, pRes] = await Promise.all([
        fetch(API_CONFIG.getDatabasesEndpoint(`reports/campaign-role-baseline/?campaign_id=${campaignId}`)),
        fetch(API_CONFIG.getDatabasesEndpoint(`reports/campaign-privilege-baseline/?campaign_id=${campaignId}`)),
      ]);
      const rJson = await rRes.json();
      const pJson = await pRes.json();
      if (rJson.status === 'success') setRoleData(rJson); else setError(rJson.error || 'Failed to load role baseline');
      if (pJson.status === 'success') setPrivData(pJson);
    } catch (err) {
      setError('Error loading baseline: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onSelectCampaign = (id) => {
    setSelectedCampaignId(id);
    setExpanded({});
    loadBaseline(id);
  };

  const levelClass = (name) => LEVEL_CLASS[(name || '').toUpperCase()] || 'text-muted';

  const filteredRoles = (roleData?.roles || []).filter(r =>
    !search || (r.role_name || '').toLowerCase().includes(search.toLowerCase())
    || (r.member_usernames || []).some(m => (m || '').toLowerCase().includes(search.toLowerCase()))
  );
  const filteredPrivs = (privData?.privileges || []).filter(p =>
    !search || (p.privilege_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const header = roleData?.campaign || privData?.campaign;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex align-items-center mb-3">
        <i className="fas fa-camera-retro fa-lg me-2 text-secondary"></i>
        <h4 className="mb-0">Campaign Role &amp; Privilege Baseline</h4>
      </div>
      <p className="text-muted">
        The immutable role and privilege state (levels, DBA certification, and role
        membership) frozen at the moment this campaign was submitted for attestation.
      </p>

      {/* Campaign picker */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <label className="form-label fw-bold">Campaign</label>
          {isLoadingCampaigns ? (
            <div className="text-muted">Loading campaigns…</div>
          ) : (
            <select
              className="form-select"
              style={{ maxWidth: 520 }}
              value={selectedCampaignId}
              onChange={(e) => onSelectCampaign(e.target.value)}
            >
              <option value="">— Select a submitted campaign —</option>
              {campaigns.map(c => (
                <option key={c.campaign_id} value={c.campaign_id}>
                  {c.campaign_name} ({c.database_type} · {c.status})
                </option>
              ))}
            </select>
          )}
          {header && (
            <div className="mt-2 small text-muted">
              Snapshot taken: {roleData?.snapshot_taken ? formatDateTime(roleData.snapshot_taken) : '—'}
              {' · '}Roles: {roleData?.count ?? 0} · Privileges: {privData?.count ?? 0}
            </div>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {isLoading && <div className="text-muted mb-3">Loading baseline…</div>}

      {selectedCampaignId && !isLoading && (
        <>
          {/* Tabs + search */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <ul className="nav nav-tabs">
              <li className="nav-item">
                <button className={`nav-link ${tab === 'roles' ? 'active' : ''}`} onClick={() => setTab('roles')}>
                  Roles ({roleData?.count ?? 0})
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${tab === 'privileges' ? 'active' : ''}`} onClick={() => setTab('privileges')}>
                  Privileges ({privData?.count ?? 0})
                </button>
              </li>
            </ul>
            <input
              className="form-control"
              style={{ maxWidth: 320 }}
              placeholder={tab === 'roles' ? 'Search role or member…' : 'Search privilege…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {tab === 'roles' && (
            <div className="card shadow-sm">
              <div className="card-body p-0 table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Role</th>
                      <th>Kind</th>
                      <th>Database</th>
                      <th>Level</th>
                      <th>Certified</th>
                      <th>Members</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRoles.map((r, i) => {
                      const key = `${r.role_name}|${r.database_name || ''}|${i}`;
                      const open = expanded[key];
                      return (
                        <React.Fragment key={key}>
                          <tr>
                            <td className="fw-semibold">{r.role_name}</td>
                            <td><span className="text-muted">{SCOPE_LABEL[r.role_scope] || r.role_scope}</span></td>
                            <td>{r.is_global ? <span className="text-info">Global</span> : (r.database_name || <span className="text-muted">—</span>)}</td>
                            <td className={levelClass(r.privilege_level_name)}>{r.privilege_level_name || '—'}</td>
                            <td>
                              {r.certified === true ? <span className="text-success">Yes</span>
                                : r.certified === false ? <span className="text-danger">No</span>
                                : <span className="text-muted">N/A</span>}
                              {r.certified_by && <span className="text-muted small"> · {r.certified_by}</span>}
                            </td>
                            <td>
                              {r.member_count > 0 ? (
                                <button className="btn btn-sm btn-link p-0" onClick={() => setExpanded({ ...expanded, [key]: !open })}>
                                  {r.member_count} member{r.member_count !== 1 ? 's' : ''} {open ? '▲' : '▼'}
                                </button>
                              ) : <span className="text-muted">0</span>}
                            </td>
                          </tr>
                          {open && r.member_count > 0 && (
                            <tr>
                              <td colSpan={6} className="bg-light">
                                <div className="p-2">
                                  <strong className="small text-muted">Members (users who held {r.role_name} at submission):</strong>
                                  <div className="mt-1">
                                    {r.member_usernames.map((m, j) => (
                                      <span key={j} className="badge bg-secondary me-1 mb-1">{m}</span>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {filteredRoles.length === 0 && (
                      <tr><td colSpan={6} className="text-center text-muted p-3">No roles in this baseline.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'privileges' && (
            <div className="card shadow-sm">
              <div className="card-body p-0 table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Privilege</th>
                      <th>Scope</th>
                      <th>Level</th>
                      <th>Certified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPrivs.map((p, i) => (
                      <tr key={i}>
                        <td className="fw-semibold">{p.privilege_name}</td>
                        <td><span className="text-muted">{p.privilege_scope || '—'}</span></td>
                        <td className={levelClass(p.privilege_level_name)}>{p.privilege_level_name || '—'}</td>
                        <td>
                          {p.certified === true ? <span className="text-success">Yes</span>
                            : p.certified === false ? <span className="text-danger">No</span>
                            : <span className="text-muted">N/A</span>}
                          {p.certified_by && <span className="text-muted small"> · {p.certified_by}</span>}
                        </td>
                      </tr>
                    ))}
                    {filteredPrivs.length === 0 && (
                      <tr><td colSpan={4} className="text-center text-muted p-3">No privileges in this baseline.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CampaignBaselineReport;
